import { inject, Injectable } from '@angular/core';
import { Client } from '@stomp/stompjs';
import { captureMessage } from '@sentry/angular';
import { filter, map, Observable, Subject } from 'rxjs';
import { WSMessageModel, WSType } from '@data-access';
import { environment } from '@shared-app-config';
import { LocalStorageService } from './local.storage.service';
import { SessionStore } from '../session/session.store';

/**
 * One malformed frame is dropped instead of throwing inside the STOMP callback, and reported to
 * Sentry without its body — a frame can carry chat text.
 */
export function parseWsMessage(body: string | undefined): WSMessageModel | null {
  if (!body) return null;

  try {
    return JSON.parse(body) as WSMessageModel;
  } catch {
    captureMessage('Dropped a WebSocket message that is not valid JSON', 'warning');
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class WsNotificationsService {
  private localStorageService = inject(LocalStorageService);

  private readonly messageCommand = 'MESSAGE';
  private readonly wsUrl = `wss://${environment.wsUrl}/ws/websocket`;
  private readonly wsDestinationUrl = '/user/queue/ws';
  private wsClient: Client | null = null;
  private readonly session = inject(SessionStore);
  private received$ = new Subject<WSMessageModel>();
  public message$: Observable<WSMessageModel> = this.received$.asObservable();

  private onConnect(client: Client): () => void {
    return () => {
      client.subscribe(
        this.wsDestinationUrl,
        (message) => {
          if (message.command === this.messageCommand) {
            const incomingMessage = parseWsMessage(message.body);

            if (incomingMessage && incomingMessage.type) {
              this.received$.next(incomingMessage);
            }
          }
        },
        {
          'auto-delete': 'true',
          ack: 'auto',
        },
      );
    };
  }

  public disconnect(): void {
    if (this.wsClient) {
      this.wsClient.deactivate();
      // A deactivated client never reconnects, so the next initConnection() needs a new one.
      this.wsClient = null;
    }
  }

  public initConnection(): void {
    if (!this.wsClient) {
      const ACCESS_TOKEN = this.localStorageService.get('accessToken');

      if (!ACCESS_TOKEN) {
        throw new Error('Access token is required for WebSocket connection');
      }

      this.wsClient = new Client({
        brokerURL: `${this.wsUrl}?access_token=${ACCESS_TOKEN}`,
        reconnectDelay: 3000,
      });
      this.wsClient.activate();
      this.wsClient.onConnect = this.onConnect(this.wsClient);
    }
  }

  public getMessage$<MessageDataType>(skipMessageForSourceTab = true): Observable<MessageDataType> {
    return this.received$.pipe(
      filter((message: WSMessageModel) => {
        const tabId = this.session.tabId();
        if (skipMessageForSourceTab) {
          return !tabId || message.tabId !== tabId;
        }

        return true;
      }),
      map((message: WSMessageModel) => message.content as MessageDataType),
      filter(Boolean),
    );
  }

  public getMessageByType$<MessageDataType>(
    type: WSType,
    skipMessageForSourceTab = true,
  ): Observable<MessageDataType | null> {
    return this.received$.pipe(
      filter((message: WSMessageModel) => {
        if (!type || message.type !== type) {
          return false;
        }
        const tabId = this.session.tabId();
        return !skipMessageForSourceTab || !tabId || message.tabId !== tabId;
      }),
      map((message: WSMessageModel) => {
        return (message.content ?? null) as MessageDataType | null;
      }),
    );
  }
}
